import type { FormState, RegisterReference } from '@/shared/types/users.types';
import type { C } from '@/utils/colorHelpers';

interface DocField {
    categoryId: number;
    label: string;
    required: boolean;
}

function buildDocFields(
    userType: string,
    categories: RegisterReference['categories'],
): DocField[] {
    const find = (...keywords: string[]) =>
        categories.find(c =>
            keywords.some(k => c.name.toLowerCase().includes(k.toLowerCase())),
        );

    if (userType === 'livreur-pro') {
        const fields: Array<{ keywords: string[]; label: string; required: boolean }> = [
            { keywords: ['identité', 'carte nationale'], label: "Carte Nationale d'Identité", required: true },
            { keywords: ['permis'], label: 'Permis de Conduire', required: true },
            { keywords: ['assurance'], label: 'Assurance Véhicule', required: true },
            { keywords: ['carte grise'], label: 'Carte Grise', required: true },
            { keywords: ['casier'], label: 'Casier Judiciaire', required: true },
        ];

        return fields
            .map(f => {
                const cat = find(...f.keywords);
                return cat ? { categoryId: cat.id, label: cat.name, required: f.required } : null;
            })
            .filter((f): f is DocField => f !== null);
    }

    if (userType === 'livreur') {
        const cni = find('identité', 'carte nationale');
        if (!cni) return [];
        return [{ categoryId: cni.id, label: cni.name, required: false }];
    }

    return [];
}

export function StepDocuments({
    form,
    setDoc,
    userType,
    reference,
    c,
}: {
    form: FormState;
    setDoc: (k: string, f: File | null) => void;
    userType: string;
    reference: RegisterReference;
    c: C;
}) {
    const docs = buildDocFields(userType, reference.categories);

    if (docs.length === 0) {
        return (
            <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">Documents</h2>
                <p className="text-sm text-gray-500">Aucun document requis pour ce type de compte.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Documents requis</h2>
            <p className="text-sm text-gray-400">Les champs marqués * sont obligatoires.</p>

            {docs.map(d => {
                const key = String(d.categoryId);
                return (
                    <div key={key} className="border border-gray-200 rounded-xl p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {d.label} {d.required && <span className="text-red-500">*</span>}
                        </label>
                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-gray-300 transition-colors">
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                id={`doc-${key}`}
                                onChange={e => setDoc(key, e.target.files?.[0] ?? null)}
                            />
                            <label htmlFor={`doc-${key}`} className="cursor-pointer">
                                <i className={`ri-upload-cloud-2-line text-2xl mb-1 block ${form.documents[key] ? 'text-green-500' : 'text-gray-300'
                                    }`} />
                                <p className="text-sm text-gray-500">
                                    {form.documents[key]
                                        ? (form.documents[key] as File).name
                                        : 'Cliquer pour télécharger'}
                                </p>
                                <p className="text-xs text-gray-300 mt-1">PDF, JPG, PNG — max 5 Mo</p>
                            </label>
                        </div>
                    </div>
                );
            })}

            <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-500 flex items-start gap-2">
                <i className="ri-shield-check-line text-green-500 text-base mt-0.5" />
                <span>Vos documents sont chiffrés et utilisés uniquement pour la vérification de votre compte.</span>
            </div>
        </div>
    );
}
